import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import Cookies from "js-cookie";
import { CalendarIcon, CheckIcon, PlusIcon } from "lucide-react";
import { CaretSortIcon } from "@radix-ui/react-icons";
import { toast } from "sonner";
import { AxiosError } from "axios";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog/alert-dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import { Button } from "@/components/ui/button/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";

import { createNewLoanForEmployeeByAdmin } from "@/config/mutation/admin";
import { UploadAttachment } from "./upload-attachement";
import { getAllEmployeesQueryOptions } from "@/config/queries/query-options";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar/calendar";

const formSchema = z.object({
  totalAmountRequested: z.coerce.number().min(0).default(0),
  attachment: z.string().optional(),
  employee: z.string(),
  date: z.date(),
  interestRate: z.coerce.number(),
  numberOfMonths: z.coerce.number(),
});

type FormValues = z.infer<typeof formSchema>;

export const CreateEmpLoan = () => {
  const queryClient = useQueryClient();

  const key = Cookies.get("key") as string;
  const [open, setOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      totalAmountRequested: 0,
      attachment: "",
      employee: undefined,
      date: undefined,
      interestRate: 0,
      numberOfMonths: 1,
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createNewLoanForEmployeeByAdmin(key),
  });

  const onSubmit: SubmitHandler<FormValues> = (values: FormValues) => {
    console.log(values);

    mutate(
      {
        totalAmountRequested: values?.totalAmountRequested,
        attachment: values?.attachment,
        employee: values?.employee,
        date: values?.date,
        interestRate: values?.interestRate,
        numberOfMonths: values?.numberOfMonths,
        totalAmountApproved: values?.totalAmountRequested,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["other-loans"],
          });

          toast.success("Loan Created!");

          setTimeout(() => {
            form.setValue("totalAmountRequested", 0);
            form.setValue("attachment", "");
          }, 1000);
          setOpen(false);
        },

        onError: (error) => {
          console.log("ERROR WHILE CREATING NEW LOAN By Admin", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  const {
    data,
    isPending: isEmpsLoading,
    isFetching,
  } = useQuery(getAllEmployeesQueryOptions(key));

  const employeesData = useMemo(() => {
    if (isPending || isFetching) {
      return [];
    }

    if (data === undefined || data?.employees === undefined) {
      return [];
    }

    const result = data?.employees?.map((emp) => {
      return {
        value: emp?._id,
        label: emp?.name,
      };
    });

    return result;
  }, [data, isEmpsLoading, isFetching]);

  return (
    <div>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger className="tracking-normal">
          <Button className="flex items-center gap-1" size="sm">
            <PlusIcon className="w-4 h-4" />
            <span>Create Loan</span>
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="max-w-4xl overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="tracking-wide">
              Create Loan
            </AlertDialogTitle>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 overflow-y-auto">
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="employee"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Employee</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  className={cn(
                                    "w-full justify-between",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {field.value
                                    ? employeesData.find(
                                        (emp) => emp.value === field.value
                                      )?.label
                                    : "Select Employee"}
                                  <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-full p-0">
                              <Command>
                                <CommandInput
                                  placeholder="Search Employee..."
                                  className="h-9"
                                />
                                <CommandEmpty>No employee found.</CommandEmpty>
                                <CommandList>
                                  <CommandGroup>
                                    {employeesData.map((emp) => (
                                      <CommandItem
                                        value={emp.label}
                                        key={emp.value}
                                        onSelect={() => {
                                          form.setValue("employee", emp.value);
                                        }}
                                      >
                                        {emp.label}
                                        <CheckIcon
                                          className={cn(
                                            "ml-auto h-4 w-4",
                                            emp.value === field.value
                                              ? "opacity-100"
                                              : "opacity-0"
                                          )}
                                        />
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      name="totalAmountRequested"
                      control={form.control}
                      render={({ field }) => {
                        return (
                          <FormItem>
                            <FormLabel>Total Loan Amount</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                {...field}
                                disabled={isPending}
                              />
                            </FormControl>
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      name="numberOfMonths"
                      control={form.control}
                      render={({ field }) => {
                        return (
                          <FormItem>
                            <FormLabel>Number of Months</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                {...field}
                                disabled={isPending}
                              />
                            </FormControl>
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      name="interestRate"
                      control={form.control}
                      render={({ field }) => {
                        return (
                          <FormItem>
                            <FormLabel>Interest Rate</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                {...field}
                                disabled={isPending}
                              />
                            </FormControl>
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={form.control}
                      name="date"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Date of Loan</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant={"outline"}
                                  className={cn(
                                    "w-full pl-3 text-left font-normal",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {field.value ? (
                                    format(field.value, "PPP")
                                  ) : (
                                    <span>Pick loan date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                            >
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                // disabled={(date) =>
                                //   date > new Date() ||
                                //   date < new Date("1900-01-01")
                                // }
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="attachment"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Upload Attachment</FormLabel>
                        <FormControl>
                          <UploadAttachment
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <AlertDialogFooter className="mt-5">
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <Button type="submit" disabled={isPending}>
                    Create
                  </Button>
                </AlertDialogFooter>
              </form>
            </Form>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
